import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, Mic } from 'lucide-react'
import { MagneticButton } from '@/components/MagneticButton'
import { gsap, ScrollTrigger, useGSAP } from '@/lib/gsap'
import { usePrefersReducedMotion } from '@/lib/useReducedMotion'
import { cn } from '@/lib/utils'
import { forHosts } from '@/data/content'

/* ================================================================== */
/*  OrbitCollageHero: a full-viewport, pinned, scroll-driven hero.     */
/*                                                                     */
/*  Beat 1 (load): 12 placeholder images pop in scattered across the   */
/*  viewport at varied sizes, staggered, with a centred headline.      */
/*  Beat 2 (scroll A): the scatter converges into an evenly spaced,    */
/*  slowly rotating ring; sizes NORMALISE to one uniform card size as  */
/*  the ring forms. Photos stay upright.                               */
/*  Beat 3 (scroll B): the ring slides left (~27% from the edge) and   */
/*  scales to ~0.8 while brand copy staggers in on the right; the      */
/*  centre headline fades. Fully reversible on scroll-up.              */
/*                                                                     */
/*  All motion runs in one gsap.ticker loop writing transforms to      */
/*  refs (no per-frame React renders); scroll progress comes from a    */
/*  single scrubbed ScrollTrigger. Reduced motion: a static composed   */
/*  layout, no pin, no rotation.                                       */
/* ================================================================== */

type Theme = 'dark' | 'light'

type Card = {
  src: string
  w: number
  h: number
  sx: number // scatter x, fraction of stage width (-0.5..0.5)
  sy: number // scatter y, fraction of stage height
  rot: number
  z: 0 | 20 // a few images float above the headline
  dx: number // entrance drift vector
  dy: number
}

/* Placeholder images (swap freely). Scatter is STRAIGHT (no tilt), sizes are
   bigger with deliberate corner overlaps (small over large), and the centre
   column stays clear for the text lockup. */
const CARDS: Card[] = [
  // ---- left frame: a loose column, only slight corner overlaps ----
  { src: '/img/gen/hero-kirtan.jpg', w: 300, h: 200, sx: -0.37, sy: -0.30, rot: 0, z: 0, dx: -55, dy: -35 },
  { src: '/img/gen/portrait-yogi-goldenhour.jpg', w: 160, h: 210, sx: -0.29, sy: -0.03, rot: 0, z: 20, dx: -30, dy: -10 },
  { src: '/img/gen/hero-photo-walk.jpg', w: 220, h: 300, sx: -0.42, sy: 0.18, rot: 0, z: 0, dx: -60, dy: 10 },
  { src: '/img/gen/portrait-dancer-motion.jpg', w: 140, h: 180, sx: -0.30, sy: 0.36, rot: 0, z: 20, dx: -35, dy: 30 },
  { src: '/img/gen/hero-stargazing.jpg', w: 190, h: 130, sx: -0.25, sy: 0.43, rot: 0, z: 0, dx: -20, dy: 55 },
  // ---- right frame: mirror of the left ----
  { src: '/img/gen/hero-book-club.jpg', w: 280, h: 190, sx: 0.37, sy: -0.30, rot: 0, z: 0, dx: 55, dy: -35 },
  { src: '/img/gen/hero-astrology.jpg', w: 150, h: 200, sx: 0.29, sy: -0.04, rot: 0, z: 20, dx: 30, dy: -10 },
  { src: '/img/gen/hero-riyaaz.jpg', w: 200, h: 280, sx: 0.42, sy: 0.18, rot: 0, z: 0, dx: 60, dy: 10 },
  { src: '/img/gen/hero-night-sky.jpg', w: 130, h: 170, sx: 0.30, sy: 0.36, rot: 0, z: 20, dx: 40, dy: 30 },
  { src: '/img/gen/hero-community-garden.jpg', w: 200, h: 140, sx: 0.24, sy: 0.43, rot: 0, z: 0, dx: 25, dy: 55 },
  // ---- top centre: held high, clear above the eyebrow ----
  { src: '/img/gen/card-zen-v2.jpg', w: 150, h: 190, sx: -0.14, sy: -0.49, rot: 0, z: 0, dx: -10, dy: -55 },
  { src: '/img/gen/card-pottery.jpg', w: 220, h: 145, sx: 0.14, sy: -0.50, rot: 0, z: 0, dx: 10, dy: -55 },
  // ---- bottom centre: held low, clear below the button ----
  { src: '/img/gen/hero-chai.jpg', w: 175, h: 235, sx: -0.06, sy: 0.50, rot: 0, z: 0, dx: 0, dy: 60 },
  { src: '/img/gen/card-cosmos.jpg', w: 150, h: 105, sx: 0.10, sy: 0.45, rot: 0, z: 20, dx: 5, dy: 50 },
]
const N = CARDS.length

/* Floating social chips (reference: "Add to Cart" / "Link in bio" pills). */
const CHIPS: { label: string; x: number; y: number }[] = []

/* Uniform card size once the ring is formed (slightly smaller in the centred
   ring; grows a touch as the wheel parks left). */
const RING_W = 100
const RING_H = 128
const SPIN_SECS = 48 // one full turn

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))
const smooth = (v: number) => { const t = clamp01(v); return t * t * (3 - 2 * t) }
const lerp = (a: number, b: number, t: number) => a + (b - a) * t

/* ---- rotating two-line lockup: plain line + lime line ----------------- */
const HERO_PAIRS: [string, string][] = [
  ['Learn from', 'verified instructors'],
  ['Build your own', 'community'],
  ['Show up to', 'real events'],
]

function RotatingLockup({ dark, line1Cls, line2Cls, tight }: { dark: boolean; line1Cls?: string; line2Cls?: string; tight?: boolean }) {
  const reduced = usePrefersReducedMotion()
  const [i, setI] = useState(0)
  useEffect(() => {
    if (reduced) return
    const t = window.setInterval(() => setI((v) => (v + 1) % HERO_PAIRS.length), 3200)
    return () => window.clearInterval(t)
  }, [reduced])

  const pair = reduced ? HERO_PAIRS[0] : HERO_PAIRS[i]

  // tight: keep the green box to a single word by pushing all but the last
  // word of the highlight up onto the plain line (so it never wraps to 2 rows)
  let plain = pair[0]
  let green = pair[1]
  if (tight) {
    const words = pair[1].split(' ')
    if (words.length > 1) {
      green = words[words.length - 1]
      plain = `${pair[0]} ${words.slice(0, -1).join(' ')}`
    }
  }

  const inner = (
    <>
      <span className={cn('block font-display font-normal leading-[1.05] tracking-[0.01em]', line1Cls ?? 'text-[clamp(1.9rem,1rem+3.2vw,3.4rem)]', dark ? 'text-cream' : 'text-ink')}>
        {plain}
      </span>
      <span className="mt-2 block">
        <span className={cn('inline-block -rotate-1 bg-lime px-[0.18em] pb-[0.07em] font-display font-normal leading-[0.95] tracking-[0.01em] text-[#141D12]', line2Cls ?? 'text-[clamp(2.2rem,1rem+5vw,5rem)]')}>
          {green}
        </span>
      </span>
    </>
  )

  if (reduced) return <h1 className="mt-3">{inner}</h1>

  return (
    <h1 className="mt-3 min-h-[2.2em]">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={i}
          initial={{ y: 22, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -16, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 26 }}
          className="block"
        >
          {inner}
        </motion.span>
      </AnimatePresence>
    </h1>
  )
}

/* on phones the pinned 320vh orbit is replaced by a calm, static stack:
   a small image cluster, the headline, then the LifeCrafter block with a
   ring of a few photos sitting ABOVE its copy. */
function useIsMobile() {
  const [mobile, setMobile] = useState(false)
  useEffect(() => {
    // Below this the orbit collage runs out of room for its fixed-width centre
    // copy, so tablets/phones get the calmer stacked hero instead.
    const mq = window.matchMedia('(max-width: 1151px)')
    const on = () => setMobile(mq.matches)
    on()
    if (mq.addEventListener) mq.addEventListener('change', on)
    else mq.addListener(on) // Safari < 14 fallback
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on)
      else mq.removeListener(on)
    }
  }, [])
  return mobile
}

/* ================================================================== */
/*  MobileOrbitHero: the phone version of the beat.                     */
/*  Beat 1 (load): one row of photos above the headline, one below.    */
/*  Beat 2 (scroll): both rows flow down/in and gather into a slowly    */
/*  turning ring while the hero copy fades away.                        */
/*  Beat 3 (scroll): the ring rises to the top third and the           */
/*  LifeCrafter copy rises in beneath it. All reversible on scroll-up.  */
/* ================================================================== */
function MobileOrbitHero({ theme }: { theme: Theme }) {
  const dark = theme === 'dark'
  const reduced = usePrefersReducedMotion()
  const section = useRef<HTMLElement>(null)
  const stage = useRef<HTMLDivElement>(null)
  const imgRefs = useRef<(HTMLDivElement | null)[]>([])
  const heroRef = useRef<HTMLDivElement>(null)
  const crafterRef = useRef<HTMLDivElement>(null)
  const progress = useRef(0)

  // eight photos: four frame the top, four the bottom (start), then they ring
  const imgs = [CARDS[0], CARDS[5], CARDS[2], CARDS[9], CARDS[7], CARDS[12], CARDS[6], CARDS[3]]
  const M = imgs.length
  // start positions as fractions of the stage (x, y); row above + row below
  const START = [
    { x: 0.14, y: 0.21 }, { x: 0.38, y: 0.17 }, { x: 0.62, y: 0.17 }, { x: 0.86, y: 0.21 },
    { x: 0.14, y: 0.85 }, { x: 0.38, y: 0.89 }, { x: 0.62, y: 0.89 }, { x: 0.86, y: 0.85 },
  ]

  /* one scrubbed ScrollTrigger feeds progress */
  useGSAP(() => {
    if (reduced || !section.current) return
    // reset to the top on (re)mount so the ring doesn't paint mid-scroll when
    // navigating back home — this child effect runs before the router reset.
    // ScrollTrigger.refresh() is deferred to useSmoothScroll (after Lenis).
    window.scrollTo(0, 0)
    progress.current = 0
    const st = ScrollTrigger.create({
      trigger: section.current,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.35,
      onUpdate: (self) => { progress.current = self.progress },
    })
    return () => st.kill()
  }, { scope: section, dependencies: [reduced] })

  /* the single render loop */
  useEffect(() => {
    if (reduced || !stage.current) return
    const stageEl = stage.current
    const t0 = performance.now()
    const tick = () => {
      const now = (performance.now() - t0) / 1000
      const w = stageEl.clientWidth
      const h = stageEl.clientHeight
      const p = progress.current

      const conv = smooth(p / 0.5)            // frame → ring
      const fade = smooth((p - 0.12) / 0.26)  // hero copy leaves
      const park = smooth((p - 0.5) / 0.4)    // ring rises, crafter enters

      const spin = conv * 42 + now * 3        // gentle, always-turning drift
      const ryc = lerp(0, -0.18, park) * h    // ring centre offset from mid
      const R = lerp(Math.min(w * 0.36, h * 0.26), Math.min(w * 0.2, h * 0.13), park)
      // bigger framing photos that shrink as they gather into the ring, then
      // shrink further as the ring parks so its copy has room
      const size = lerp(80, 58, conv) * lerp(1, 0.82, park)

      imgs.forEach((_, i) => {
        const el = imgRefs.current[i]
        if (!el) return
        const sx = (START[i].x - 0.5) * w
        const sy = (START[i].y - 0.5) * h
        const ang = ((i / M) * 360 + spin) * (Math.PI / 180) - Math.PI / 2
        const rx = Math.cos(ang) * R
        const ry = ryc + Math.sin(ang) * R
        const x = lerp(sx, rx, conv)
        const y = lerp(sy, ry, conv)
        el.style.width = `${size}px`
        el.style.height = `${size * 1.22}px`
        el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`
      })

      if (heroRef.current) {
        heroRef.current.style.opacity = String(1 - fade)
        heroRef.current.style.transform = `translate(-50%, -50%) translateY(${-fade * 26}px)`
        heroRef.current.style.pointerEvents = fade > 0.5 ? 'none' : ''
      }
      if (crafterRef.current) {
        const o = smooth((p - 0.56) / 0.3)
        crafterRef.current.style.opacity = String(o)
        crafterRef.current.style.transform = `translateX(-50%) translateY(${(1 - o) * 26}px)`
        crafterRef.current.style.pointerEvents = o > 0.5 ? 'auto' : 'none'
      }
    }
    gsap.ticker.add(tick)
    return () => gsap.ticker.remove(tick)
  }, [reduced])

  const softMute = dark ? 'text-cream/85' : 'text-ink/70'

  /* reduced motion: a calm static stack, no pin */
  if (reduced) {
    return (
      <section id="top" className="relative overflow-hidden px-5 pb-16 pt-28">
        <div className="mx-auto grid max-w-xs grid-cols-3 gap-2.5">
          {imgs.map((c, i) => (
            <div key={c.src + i} className={cn('aspect-[4/5] overflow-hidden rounded-2xl shadow-lg', dark ? 'ring-1 ring-white/12' : 'ring-1 ring-ink/10')}>
              <img src={c.src} alt="" className="h-full w-full object-cover" loading="eager" />
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <p className={cn('font-sans text-[13px] font-bold uppercase tracking-[0.28em]', dark ? 'text-cream-mute' : 'text-ink-mute')}>Social life, minus the media</p>
          <RotatingLockup dark={dark} line1Cls="text-[2.2rem]" line2Cls="text-[3rem]" tight />
          <p className={cn('mx-auto mt-5 max-w-xs text-[15px] leading-relaxed', softMute)}>LifeCraft4U turns what you love into who you know. Real circles, real events. The algorithm is: humans.</p>
        </div>
        <MobileCrafterCopy dark={dark} softMute={softMute} className="mt-20 text-center" />
      </section>
    )
  }

  return (
    <section ref={section} id="top" className="relative h-[240vh]">
      <div ref={stage} className="sticky top-0 h-screen overflow-hidden">
        {imgs.map((c, i) => (
          <div
            key={c.src + i}
            ref={(el) => { imgRefs.current[i] = el }}
            className="pointer-events-none absolute left-1/2 top-1/2 z-0 will-change-transform"
            style={{ width: 74, height: 90, transform: `translate(-50%, -50%) translate(${(START[i].x - 0.5) * 100}vw, ${(START[i].y - 0.5) * 100}vh)` }}
            aria-hidden="true"
          >
            <div className={cn('h-full w-full overflow-hidden rounded-xl shadow-lg', dark ? 'ring-1 ring-white/12' : 'ring-1 ring-ink/10')}>
              <img src={c.src} alt="" className="h-full w-full object-cover" loading="eager" draggable={false} />
            </div>
          </div>
        ))}

        {/* Beat 1: centred hero copy — bigger headline, smaller body */}
        <div ref={heroRef} className="absolute left-1/2 top-1/2 z-10 w-full max-w-md px-4 text-center" style={{ transform: 'translate(-50%, -50%)' }}>
          <p className={cn('font-sans text-[13px] font-bold uppercase tracking-[0.28em]', dark ? 'text-cream-mute' : 'text-ink-mute')}>Social life, minus the media</p>
          <RotatingLockup dark={dark} line1Cls="text-[2.2rem]" line2Cls="text-[3rem]" tight />
          <p className={cn('mx-auto mt-5 max-w-xs text-[15px] leading-relaxed', softMute)}>
            LifeCraft4U turns what you love into who you know. Real circles, real events. The algorithm is: humans.
          </p>
          <div className="mt-6 flex justify-center">
            <MagneticButton href="/communities" data-cursor="join" className={cn('inline-flex items-center gap-2 rounded-pill px-6 py-3 text-[15px] xl:px-7 xl:py-3.5 xl:text-[16px] font-bold', dark ? 'bg-lime text-night' : 'bg-ink text-cream')}>
              Explore communities <ArrowRight size={17} aria-hidden="true" />
            </MagneticButton>
          </div>
        </div>

        {/* Beat 3: the LifeCrafter copy rises in beneath the ring */}
        <div ref={crafterRef} className="absolute left-1/2 top-[47%] z-20 w-full max-w-sm px-4 text-center" style={{ opacity: 0, transform: 'translateX(-50%)' }}>
          <MobileCrafterCopy dark={dark} softMute={softMute} />
        </div>
      </div>
    </section>
  )
}

/* shared LifeCrafter copy block for the mobile hero (scroll + reduced) */
function MobileCrafterCopy({ dark, softMute, className }: { dark: boolean; softMute: string; className?: string }) {
  return (
    <div className={className}>
      <p className={cn('label inline-flex items-center gap-2', softMute)}>
        <Mic size={13} aria-hidden="true" /> {forHosts.eyebrow}
      </p>
      <h2 className={cn('mx-auto mt-4 max-w-[14ch] font-display text-[2.3rem] font-normal leading-[1.02] tracking-[0.01em]', dark ? 'text-cream' : 'text-ink')}>
        Your craft deserves a{' '}
        <span className="inline-block -rotate-2 rounded-[0.28em] bg-coral px-[0.2em] pb-[0.06em] text-white">community</span>.
      </h2>
      <p className={cn('mx-auto mt-4 max-w-xs text-[15px] leading-relaxed', dark ? 'text-cream' : 'text-ink')}>
        Teach what you love. Run programs, host events, earn predictably. You hold the room.
      </p>
      <MagneticButton href="/lifecrafter-program" data-cursor="apply" className={cn('mt-7 inline-flex rounded-pill px-6 py-3 text-[15px] xl:px-7 xl:py-3.5 xl:text-[16px] font-bold', dark ? 'bg-cream text-night' : 'bg-ink text-cream')}>
        {forHosts.cta}
      </MagneticButton>
    </div>
  )
}

export function OrbitCollageHero({ theme = 'dark' }: { theme?: Theme }) {
  const reduced = usePrefersReducedMotion()
  const mobile = useIsMobile()
  const section = useRef<HTMLElement>(null)
  const stage = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const chipRefs = useRef<(HTMLDivElement | null)[]>([])
  const headlineRef = useRef<HTMLDivElement>(null)
  const copyRef = useRef<HTMLDivElement>(null)
  const copyItemRefs = useRef<(HTMLElement | null)[]>([])
  const progress = useRef(0)

  const dark = theme === 'dark'

  /* one scrubbed ScrollTrigger feeds progress */
  useGSAP(() => {
    if (reduced || !section.current) return
    // On return navigation React runs this child effect BEFORE the router's
    // parent scroll-reset, so the page is still scrolled down from the last
    // page. Force the top here and reset progress so the hero replays its
    // entrance instead of painting mid-scroll ("weird formats"). The actual
    // ScrollTrigger.refresh() happens in useSmoothScroll AFTER Lenis re-inits.
    window.scrollTo(0, 0)
    progress.current = 0
    const st = ScrollTrigger.create({
      trigger: section.current,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.35,
      onUpdate: (self) => { progress.current = self.progress },
    })
    return () => st.kill()
  }, { scope: section, dependencies: [reduced] })

  /* the single render loop */
  useEffect(() => {
    if (reduced || !stage.current) return
    const stageEl = stage.current
    const t0 = performance.now()

    const tick = () => {
      const now = (performance.now() - t0) / 1000
      const w = stageEl.clientWidth
      const h = stageEl.clientHeight
      const p = progress.current

      /* Timeline:
         0.00-0.14  chips leave one by one (before anything moves)
         0.14-0.45  scatter converges into the ring (cards angle inwards)
         0.46-0.62  centre text fades + zooms backwards, ring keeps revolving
         0.62-0.90  ring drifts left until exactly half is visible, radius grows
         0.70-1.00  host copy staggers in on the right                        */
      const pA = smooth((p - 0.14) / 0.31)
      const textOut = smooth((p - 0.34) / 0.18)
      const pB = smooth((p - 0.62) / 0.28)

      // Scale the whole scatter as one coherent unit, sized to the TIGHTER of
      // width and height: it shrinks on small OR short screens (so the images
      // never crowd the centre copy) and GROWS on large ones. `spreadW`/`spreadH`
      // cap how far the images fan out so a wide monitor gets a bigger, centred
      // collage instead of images glued to the far edges with an empty middle.
      // height responds steeply: a short window (laptop with browser chrome)
      // has little room above/below the centre copy, so the art shrinks fast.
      const hFit = (h - 470) / 330 // 800→1.0, 700→0.70, 640→0.52
      // 0.9025: images trimmed 5% twice over globally (0.95 × 0.95).
      // desktopTrim: a further 5% off on desktop only (w >= 1024), per client.
      const desktopTrim = w >= 1024 ? 0.95 : 1
      const sizeMul = 0.9025 * desktopTrim * Math.min(1.12, Math.max(0.5, Math.min(w / 1280, hFit)))
      const spreadW = Math.min(w, 1280 * 1.12)
      const spreadH = Math.min(h, 820 * 1.12)

      // ring geometry: a TRUE circle throughout. It parks left with ~30% of the
      // wheel off-screen; the parked radius is capped so the full circle still
      // fits vertically (no card clipped top or bottom).
      const growth = 1 + 0.12 * pB // cards grow a touch as the wheel parks
      const R2 = Math.min(w * 0.36, h * 0.5 - RING_H / 2 - 18)
      const R3 = Math.min(w * 0.32, h * 0.5 - (RING_H * growth) / 2 - 24)
      const R = lerp(R2, R3, pB)
      const cx = lerp(0, R3 * 0.4 - w / 2, pB) // centre sits 0.4R from the left edge: ~30% clipped
      const ringFade = 1 - 0.12 * pB
      const spin = (now / SPIN_SECS) * 360 * pA

      CARDS.forEach((c, i) => {
        const el = cardRefs.current[i]
        if (!el) return

        // Beat 1: staggered entrance (straight, no tilt)
        const e = smooth((now - 0.25 - i * 0.07) / 0.65)
        // When the window is short OR narrow the centred copy eats into the
        // scatter, so fan the centre-column images out to the margins (keep at
        // least `minFrac` of the spread away from centre) to leave a clear lane.
        const tight = clamp01((1 - Math.min(hFit, w / 1280)) / 0.3)
        const minFrac = 0.3 * tight
        const dir = Math.sign(c.sx) || (i % 2 ? 1 : -1)
        const sxFrac = Math.abs(c.sx) >= minFrac ? c.sx : dir * minFrac
        const sx = sxFrac * spreadW + (1 - e) * c.dx
        // nudge the top-row scatter down so the fixed navbar doesn't clip it;
        // the offset grows with the cards so bigger art on large screens still
        // clears the bar.
        const navClear = c.sy < -0.28 ? 110 + 55 * sizeMul : 0
        const sy = c.sy * spreadH + (1 - e) * c.dy + navClear

        // ring slot; in the ring every card points INWARDS (top edge to centre)
        const angDeg = (i / N) * 360 + spin
        const ang = angDeg * (Math.PI / 180)
        const rxp = cx + Math.cos(ang) * R
        const ryp = Math.sin(ang) * R
        const inward = angDeg + 90

        const x = lerp(sx, rxp, pA)
        const y = lerp(sy, ryp, pA)
        const rot = lerp(0, inward, pA)
        const scale = 0.6 + 0.4 * e

        // sizes: bigger in the scatter, normalised (smaller) in the ring
        const cw = lerp(c.w * sizeMul, RING_W * sizeMul * growth, pA)
        const ch = lerp(c.h * sizeMul, RING_H * sizeMul * growth, pA)

        el.style.opacity = String(e * ringFade)
        el.style.width = `${cw}px`
        el.style.height = `${ch}px`
        el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${rot}deg) scale(${scale})`
      })

      // chips: pop in after the images, then leave ONE BY ONE before the ring forms
      CHIPS.forEach((c, k) => {
        const el = chipRefs.current[k]
        if (!el) return
        const eIn = smooth((now - 0.85 - k * 0.14) / 0.5)
        const eOut = smooth((p - (0.015 + k * 0.03)) / 0.045)
        const bob = Math.sin(now * 1.3 + k * 1.7) * 6
        el.style.opacity = String(eIn * (1 - eOut))
        el.style.transform = `translate(-50%, -50%) translate(${c.x * w}px, ${c.y * h + bob}px) scale(${(0.7 + 0.3 * eIn) * (1 - 0.25 * eOut)})`
      })

      // centre text: in with entrance; fades + zooms BACKWARDS once the ring is formed
      if (headlineRef.current) {
        const he = smooth((now - 0.5) / 0.7)
        const ho = he * (1 - textOut)
        headlineRef.current.style.opacity = String(ho)
        headlineRef.current.style.transform =
          `translate(-50%, -50%) translateY(${(1 - he) * 24}px) scale(${1 - 0.22 * textOut})`
        headlineRef.current.style.pointerEvents = ho < 0.4 ? 'none' : ''
      }

      // right copy: staggered in while the ring parks on the left
      copyItemRefs.current.forEach((el, k) => {
        if (!el) return
        const o = smooth((p - (0.7 + k * 0.05)) / 0.16)
        el.style.opacity = String(o)
        el.style.transform = `translateX(${(1 - o) * 56}px)`
      })
      if (copyRef.current) copyRef.current.style.pointerEvents = p > 0.8 ? 'auto' : 'none'
    }

    gsap.ticker.add(tick)
    return () => gsap.ticker.remove(tick)
  }, [reduced])

  /* ---- Beat 1: the JOINERS. Option D's content and fonts (Tanker via
     font-opt-d, Hanken body, mono labels), in the reference's three-tier
     lockup with the acid-lime highlight. ---- */
  const headline = (
    <div className="hero-copy text-center">
      <RotatingLockup dark={dark} />
      <p className={cn('mx-auto mt-6 max-w-xl text-lg leading-relaxed', dark ? 'text-cream-mute' : 'text-ink-mute')}>
        LifeCraft4U turns what you love into who you know. Real circles, real
        events, people at every age and stage. The algorithm is: humans.
      </p>
      <div className="mt-7 flex justify-center">
        <MagneticButton
          href="/communities"
          data-cursor="join"
          className={cn(
            'inline-flex items-center gap-2 rounded-pill px-6 py-3 text-[15px] xl:px-8 xl:py-4 xl:text-[17px] font-bold',
            dark ? 'bg-lime text-night' : 'bg-ink text-cream',
          )}
        >
          Explore communities <ArrowRight size={18} aria-hidden="true" />
        </MagneticButton>
      </div>
    </div>
  )

  /* ---- Beat 3: the LIFECRAFTERS. The hosts' side of LifeCraft4U. ---- */
  const sideCopy = (
    <div className="hero-copy max-w-md">
      <p
        ref={(el) => { copyItemRefs.current[0] = el as HTMLElement }}
        className={cn('label flex items-center gap-2', dark ? 'text-cream-mute' : 'text-ink-mute')}
        style={reduced ? undefined : { opacity: 0 }}
      >
        <Mic size={13} aria-hidden="true" /> {forHosts.eyebrow}
      </p>
      <h2
        ref={(el) => { copyItemRefs.current[1] = el }}
        className={cn('mt-5 font-display text-[clamp(2.4rem,1.2rem+4.4vw,4.8rem)] font-normal leading-[1] tracking-[0.01em]', dark ? 'text-cream' : 'text-ink')}
        style={reduced ? undefined : { opacity: 0 }}
      >
        Your craft deserves a{' '}
        <span className="inline-block -rotate-2 rounded-[0.28em] bg-coral px-[0.2em] pb-[0.06em] text-white">community</span>.
      </h2>
      <p
        ref={(el) => { copyItemRefs.current[2] = el as HTMLElement }}
        className={cn('mt-5 text-lg leading-relaxed', dark ? 'text-cream' : 'text-ink')}
        style={reduced ? undefined : { opacity: 0 }}
      >
        Teach what you love. Run programs, host events, earn predictably. We handle
        payments, RSVPs and the boring parts. You hold the room.
      </p>
      <div ref={(el) => { copyItemRefs.current[3] = el as HTMLElement }} style={reduced ? undefined : { opacity: 0 }}>
        <MagneticButton
          href="/lifecrafter-program"
          data-cursor="apply"
          className={cn(
            'mt-8 rounded-pill px-6 py-3 text-[15px] xl:px-8 xl:py-4 xl:text-[17px] font-bold',
            dark ? 'bg-cream text-night' : 'bg-ink text-cream',
          )}
        >
          {forHosts.cta}
        </MagneticButton>
        <p className={cn('label mt-4', dark ? 'text-cream' : 'text-ink')}>
          Explorers join circles. LifeCrafters host them.
        </p>
      </div>
    </div>
  )

  /* ---- mobile: its own scroll-driven mini-orbit (below) ---- */
  if (mobile) return <MobileOrbitHero theme={theme} />

  /* ---- reduced motion: static, composed, no pin ---- */
  if (reduced) {
    return (
      <section id="top" className="relative overflow-hidden px-5 pb-16 pt-32">
        <div className="container-lc mb-16">{headline}</div>
        <div className="container-lc grid items-center gap-12 lg:grid-cols-2">
          <div className="relative mx-auto aspect-square w-full max-w-md">
            {CARDS.map((c, i) => {
              const ang = (i / N) * 2 * Math.PI
              return (
                <div
                  key={c.src + i}
                  className="absolute overflow-hidden rounded-xl shadow-lg"
                  style={{
                    width: RING_W * 0.72,
                    height: RING_H * 0.72,
                    left: `${50 + Math.cos(ang) * 42}%`,
                    top: `${50 + Math.sin(ang) * 42}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <img src={c.src} alt="" className="h-full w-full object-cover" loading="eager" />
                </div>
              )
            })}
          </div>
          {sideCopy}
        </div>
      </section>
    )
  }

  return (
    <section ref={section} id="top" className="relative h-[320vh]">
      <div ref={stage} className="sticky top-0 h-screen overflow-hidden">
        {/* the collage / ring */}
        {CARDS.map((c, i) => (
          <div
            key={c.src + i}
            ref={(el) => { cardRefs.current[i] = el }}
            className={cn('pointer-events-none absolute left-1/2 top-1/2 will-change-transform', c.z === 20 ? 'z-20' : 'z-0')}
            style={{ width: c.w, height: c.h, opacity: 0 }}
            aria-hidden="true"
          >
            <div className={cn('h-full w-full overflow-hidden rounded-2xl shadow-xl', dark ? 'ring-2 ring-white/12' : 'ring-2 ring-ink/10')}>
              <img src={c.src} alt="" className="h-full w-full object-cover" loading="eager" draggable={false} />
            </div>
          </div>
        ))}

        {/* floating social chips, reference-style */}
        {CHIPS.map((c, k) => (
          <div
            key={c.label}
            ref={(el) => { chipRefs.current[k] = el }}
            className="pointer-events-none absolute left-1/2 top-1/2 z-30 whitespace-nowrap rounded-pill bg-cobalt px-3.5 py-1.5 font-sans text-[13px] font-semibold text-white shadow-lg"
            style={{ opacity: 0 }}
            aria-hidden="true"
          >
            {c.label}
          </div>
        ))}

        {/* Beat 1 + 2: centred headline, layered among the images */}
        <div
          ref={headlineRef}
          className="absolute left-1/2 top-1/2 z-10 w-full max-w-3xl px-6"
          style={{ opacity: 0, transform: 'translate(-50%, -50%)' }}
        >
          {headline}
        </div>

        {/* Beat 3: brand copy on the right */}
        <div
          ref={copyRef}
          className="absolute inset-y-0 right-0 z-30 flex w-full items-center justify-start px-6 md:w-1/2 md:pl-[2vw]"
          style={{ pointerEvents: 'none' }}
        >
          {sideCopy}
        </div>
      </div>
    </section>
  )
}

export default OrbitCollageHero
